import httpClient from '../../../shared/lib/httpClient';
import { ENDPOINTS } from '../../../shared/services/api/endpoints';
import { useSidebarVisibilityStore } from '../../../shared/store/sidebarVisibilityStore';

/**
 * Fetch user's companies after login
 * Returns either a single company or a list of companies
 * @returns {Promise<{ companies: Array<{ id: string|number, name: string }>}>}
 */
export async function getUserCompanies() {
  const response = await httpClient.get(ENDPOINTS.AUTH.COMPANIES);
  return response.data.data;
}

/**
 * Select a company and set it in the backend (cookie/context)
 * @param {string|number} firmId - The ID of the firm to select
 * @returns {Promise<{ company: { id: string|number, name: string } }>}
 */
export async function selectCompany(firmId) {
  const response = await httpClient.post(ENDPOINTS.AUTH.SELECT_COMPANY, { firmId });
  // Firm context changed — sidebar visibility must be refetched for the new firm
  useSidebarVisibilityStore.getState().refresh();
  return response.data;
}

/**
 * Clear the active firm cookie on logout
 * @returns {Promise<void>}
 */
export async function clearActiveFirm() {
  await httpClient.delete(ENDPOINTS.AUTH.CLEAR_COMPANY);
}

/**
 * Select a site and set it in the backend (cookie/context). The backend validates the site belongs to
 * the user and to the active firm, so call this AFTER `selectCompany` when switching customers.
 * @param {number} haloPsaSiteId - The HaloPSA site ID to make active
 * @returns {Promise<void>}
 */
export async function selectSite(haloPsaSiteId) {
  await httpClient.post(ENDPOINTS.AUTH.SELECT_SITE, { haloPsaSiteId });
  // Site context changed — roles (and so sidebar visibility) are resolved per site
  useSidebarVisibilityStore.getState().refresh();
}

/**
 * Clear the active site cookie (customer switch to a firm with no sites, or logout)
 * @returns {Promise<void>}
 */
export async function clearActiveSite() {
  await httpClient.delete(ENDPOINTS.AUTH.CLEAR_SITE);
}

/**
 * Sign up a new customer account
 * @param {object} signupData - The signup data
 * @param {string} signupData.companyName - Company name
 * @param {string} signupData.firstName - First name
 * @param {string} signupData.lastName - Last name
 * @param {string} signupData.email - Email address
 * @returns {Promise<{ userId: number, firmId: number, email: string, message: string }>}
 */
export async function signup(signupData) {
  const response = await httpClient.post(ENDPOINTS.ACCOUNT.SIGNUP, signupData, {
    timeout: 60000,
  });
  return response.data;
}

export async function checkSignupUsername(username) {
  const response = await httpClient.get(ENDPOINTS.ACCOUNT.CHECK_USERNAME, { params: { username } });
  return response.data;
}

/**
 * Check if email already exists in the system
 * @param {string} email - Email address to check
 * @returns {Promise<{ exists: boolean }>}
 */
export async function checkEmailExists(email) {
  const response = await httpClient.get(ENDPOINTS.ACCOUNT.CHECK_EMAIL, { params: { email } });
  return response.data;
}

// Firm-subdomain calls moved to `@shared/services/subdomainService` so `profile` no longer imports
// from `auth`. Re-exported here for backward compatibility with any remaining `authService` callers.
export { checkSubdomain, generateSubdomain } from '@shared/services/subdomainService';

/**
 * Change user password
 * @param {Object} passwordData - Password data with newPassword
 * @returns {Promise<{ success: boolean, message?: string }>}
 */
export async function changePassword(passwordData) {
  try {
    const response = await httpClient.post(ENDPOINTS.IDENTITY.CHANGE_PASSWORD, passwordData, {
      timeout: 30000 // 30 seconds timeout for password change
    });
    
    const message = response.data?.message || 'Password changed successfully';
    return { success: true, message };
  } catch (error) {
    return { success: false, message: error.response?.data?.message || 'Failed to change password.' };
  }
}

/**
 * Get user roles and permissions
 * @returns {Promise<{ roles: Array<string>, permissions: Array<string> }>}
 */
export async function getRoles() {
  const response = await httpClient.get(ENDPOINTS.IDENTITY.ROLES);
  // The endpoint now returns the standard ApiResponse envelope ({ success, data: { roles, ... } }).
  // Fall back to the body itself so a frontend deployed ahead of the backend keeps working
  // against the old raw { roles, permissions } shape.
  return response.data?.data ?? response.data;
}

/**
 * Get all available user roles from database
 * @returns {Promise<Array<{userRoleId: number, roleName: string, roleCode: string}>>}
 */
export async function getUserRoles() {
  const response = await httpClient.get(ENDPOINTS.IDENTITY.USER_ROLES);
  return response.data;
}

/**
 * Verify password setup and activate user account
 * Called on first login after user sets password in Keycloak
 * @returns {Promise<{ success: boolean, message?: string }>}
 */
export async function verifyPasswordSetup() {
  try {
    const response = await httpClient.post(ENDPOINTS.IDENTITY.VERIFY_PASSWORD_SETUP);
    return { success: true, message: response.data?.message || 'User activated successfully.' };
  } catch (error) {
    console.warn('[Password Setup] Failed to verify password setup:', error);
    return { success: false, message: error.response?.data?.message || 'Failed to verify password setup.' };
  }
}
